import { InputFrame } from './physics.js';
import { Quat, Vec3, clamp } from './math.js';

// ── Binary wire format ──────────────────────────────────────────────────────
// All multi-byte values little-endian. First byte of every binary frame = type.

export const BIN_INPUT = 1;
export const BIN_SNAPSHOT = 2;

// ── quaternion compression: "smallest three", 10 bits per component ────────

export function packQuat(q: Quat): number {
  const comps = [q.x, q.y, q.z, q.w];
  let largest = 0;
  for (let i = 1; i < 4; i++) if (Math.abs(comps[i]) > Math.abs(comps[largest])) largest = i;
  const sign = comps[largest] < 0 ? -1 : 1;
  const scale = 511 / Math.SQRT1_2; // remaining components are within ±1/√2
  let packed = largest >>> 0;
  let shift = 2;
  for (let i = 0; i < 4; i++) {
    if (i === largest) continue;
    const v = Math.round(clamp(comps[i] * sign * scale, -511, 511)) + 511; // 0..1022 in 10 bits
    packed |= v << shift;
    shift += 10;
  }
  return packed >>> 0;
}

export function unpackQuat(packed: number, out: Quat): Quat {
  const largest = packed & 3;
  const scale = Math.SQRT1_2 / 511;
  const vals: number[] = [];
  let shift = 2;
  for (let i = 0; i < 3; i++) {
    vals.push(((packed >>> shift) & 1023) - 511);
    shift += 10;
  }
  const comps = [0, 0, 0, 0];
  let sumSq = 0;
  let vi = 0;
  for (let i = 0; i < 4; i++) {
    if (i === largest) continue;
    comps[i] = vals[vi++] * scale;
    sumSq += comps[i] * comps[i];
  }
  comps[largest] = Math.sqrt(Math.max(0, 1 - sumSq));
  return out.set(comps[0], comps[1], comps[2], comps[3]).normalize();
}

// ── input packets (client → server) ────────────────────────────────────────
// Redundant frames: each packet carries the last N frames so packet loss rarely
// costs the server an input.

export interface InputPacket {
  /** Sequence number of the LAST frame in the packet. */
  seq: number;
  frames: InputFrame[];
}

const BTN_BOOST = 1;
const BTN_RESPAWN = 2;

export function encodeInputPacket(pkt: InputPacket): ArrayBuffer {
  const n = Math.min(pkt.frames.length, 8);
  const buf = new ArrayBuffer(1 + 4 + 1 + n * 6);
  const dv = new DataView(buf);
  dv.setUint8(0, BIN_INPUT);
  dv.setUint32(1, pkt.seq >>> 0, true);
  dv.setUint8(5, n);
  let off = 6;
  for (let i = pkt.frames.length - n; i < pkt.frames.length; i++) {
    const f = pkt.frames[i];
    dv.setInt8(off, Math.round(clamp(f.steer, -1, 1) * 127));
    dv.setInt8(off + 1, Math.round(clamp(f.pitch, -1, 1) * 127));
    dv.setUint8(off + 2, Math.round(clamp(f.throttle, 0, 1) * 255));
    dv.setUint8(off + 3, Math.round(clamp(f.brakeL, 0, 1) * 255));
    dv.setUint8(off + 4, Math.round(clamp(f.brakeR, 0, 1) * 255));
    dv.setUint8(off + 5, (f.boost ? BTN_BOOST : 0) | (f.respawn ? BTN_RESPAWN : 0));
    off += 6;
  }
  return buf;
}

export function decodeInputPacket(dv: DataView): InputPacket | null {
  if (dv.byteLength < 6 || dv.getUint8(0) !== BIN_INPUT) return null;
  const seq = dv.getUint32(1, true);
  const n = dv.getUint8(5);
  if (n > 8 || dv.byteLength < 6 + n * 6) return null;
  const frames: InputFrame[] = [];
  let off = 6;
  for (let i = 0; i < n; i++) {
    const buttons = dv.getUint8(off + 5);
    frames.push({
      steer: dv.getInt8(off) / 127,
      pitch: dv.getInt8(off + 1) / 127,
      throttle: dv.getUint8(off + 2) / 255,
      brakeL: dv.getUint8(off + 3) / 255,
      brakeR: dv.getUint8(off + 4) / 255,
      boost: (buttons & BTN_BOOST) !== 0,
      respawn: (buttons & BTN_RESPAWN) !== 0,
    });
    off += 6;
  }
  return { seq, frames };
}

// ── snapshots (server → client) ─────────────────────────────────────────────

export const SHIP_FLAG_GROUNDED = 1;
export const SHIP_FLAG_BOOSTING = 2;
export const SHIP_FLAG_DRIFTING = 4;
export const SHIP_FLAG_FINISHED = 8;
export const SHIP_FLAG_ELIMINATED = 16;
export const SHIP_FLAG_RESPAWNING = 32;
export const SHIP_FLAG_INJUMP = 64;
export const SHIP_FLAG_WRONGWAY = 128;

export interface SnapshotShip {
  index: number;
  flags: number;
  pos: Vec3;
  vel: Vec3;
  quatPacked: number;
  boostEnergy: number; // 0..1
  lap: number;
  nextCheckpoint: number;
  steer: number; // -1..1 (for remote lean/animation)
  wallContact: number; // 0..1
}

export interface Snapshot {
  tick: number;
  /** Seq of the last input the server processed from THIS client (reconciliation anchor). */
  lastInputSeq: number;
  ships: SnapshotShip[];
  standings: number[];
}

export const SNAP_SHIP_BYTES = 1 + 1 + 12 + 6 + 4 + 1 + 1 + 1 + 1 + 1; // 29

export function encodeSnapshot(snap: Snapshot): ArrayBuffer {
  const n = snap.ships.length;
  const buf = new ArrayBuffer(1 + 4 + 4 + 1 + n * SNAP_SHIP_BYTES + 1 + snap.standings.length);
  const dv = new DataView(buf);
  dv.setUint8(0, BIN_SNAPSHOT);
  dv.setUint32(1, snap.tick >>> 0, true);
  dv.setUint32(5, snap.lastInputSeq >>> 0, true);
  dv.setUint8(9, n);
  let off = 10;
  for (const s of snap.ships) {
    dv.setUint8(off, s.index);
    dv.setUint8(off + 1, s.flags);
    dv.setFloat32(off + 2, s.pos.x, true);
    dv.setFloat32(off + 6, s.pos.y, true);
    dv.setFloat32(off + 10, s.pos.z, true);
    dv.setInt16(off + 14, Math.round(clamp(s.vel.x, -300, 300) * 100), true);
    dv.setInt16(off + 16, Math.round(clamp(s.vel.y, -300, 300) * 100), true);
    dv.setInt16(off + 18, Math.round(clamp(s.vel.z, -300, 300) * 100), true);
    dv.setUint32(off + 20, s.quatPacked, true);
    dv.setUint8(off + 24, Math.round(clamp(s.boostEnergy, 0, 1) * 255));
    dv.setUint8(off + 25, s.lap & 0xff);
    dv.setUint8(off + 26, s.nextCheckpoint & 0xff);
    dv.setInt8(off + 27, Math.round(clamp(s.steer, -1, 1) * 127));
    dv.setUint8(off + 28, Math.round(clamp(s.wallContact, 0, 1) * 255));
    off += SNAP_SHIP_BYTES;
  }
  dv.setUint8(off, snap.standings.length);
  off += 1;
  for (const idx of snap.standings) dv.setUint8(off++, idx);
  return buf;
}

export function decodeSnapshot(dv: DataView): Snapshot | null {
  if (dv.byteLength < 10 || dv.getUint8(0) !== BIN_SNAPSHOT) return null;
  const tick = dv.getUint32(1, true);
  const lastInputSeq = dv.getUint32(5, true);
  const n = dv.getUint8(9);
  if (dv.byteLength < 10 + n * SNAP_SHIP_BYTES + 1) return null;
  const ships: SnapshotShip[] = [];
  let off = 10;
  for (let i = 0; i < n; i++) {
    ships.push({
      index: dv.getUint8(off),
      flags: dv.getUint8(off + 1),
      pos: new Vec3(
        dv.getFloat32(off + 2, true),
        dv.getFloat32(off + 6, true),
        dv.getFloat32(off + 10, true)
      ),
      vel: new Vec3(
        dv.getInt16(off + 14, true) / 100,
        dv.getInt16(off + 16, true) / 100,
        dv.getInt16(off + 18, true) / 100
      ),
      quatPacked: dv.getUint32(off + 20, true),
      boostEnergy: dv.getUint8(off + 24) / 255,
      lap: dv.getUint8(off + 25),
      nextCheckpoint: dv.getUint8(off + 26),
      steer: dv.getInt8(off + 27) / 127,
      wallContact: dv.getUint8(off + 28) / 255,
    });
    off += SNAP_SHIP_BYTES;
  }
  const sc = dv.getUint8(off);
  off += 1;
  if (dv.byteLength < off + sc) return null;
  const standings: number[] = [];
  for (let i = 0; i < sc; i++) standings.push(dv.getUint8(off + i));
  return { tick, lastInputSeq, ships, standings };
}

// ── ghost recordings (local persistence, versioned) ─────────────────────────

export interface GhostData {
  version: number;
  trackId: string;
  trackSeed: string | null;
  vehicleId: string;
  paintId: string;
  name: string;
  /** Total race time in ticks. */
  totalTicks: number;
  /** Recorded every `stride` ticks. */
  stride: number;
  /** Packed frames: x,y,z (f32) + packed quat (u32) per sample. */
  frames: ArrayBuffer;
}

export function ghostFrameCount(g: GhostData): number {
  return Math.floor(g.frames.byteLength / 16);
}

export function encodeGhostFrames(samples: { pos: Vec3; quat: Quat }[]): ArrayBuffer {
  const buf = new ArrayBuffer(samples.length * 16);
  const dv = new DataView(buf);
  samples.forEach((s, i) => {
    dv.setFloat32(i * 16, s.pos.x, true);
    dv.setFloat32(i * 16 + 4, s.pos.y, true);
    dv.setFloat32(i * 16 + 8, s.pos.z, true);
    dv.setUint32(i * 16 + 12, packQuat(s.quat), true);
  });
  return buf;
}

export function readGhostFrame(
  g: GhostData,
  i: number,
  outPos: Vec3,
  outQuat: Quat
): boolean {
  const n = ghostFrameCount(g);
  if (i < 0 || i >= n) return false;
  const dv = new DataView(g.frames);
  outPos.set(dv.getFloat32(i * 16, true), dv.getFloat32(i * 16 + 4, true), dv.getFloat32(i * 16 + 8, true));
  unpackQuat(dv.getUint32(i * 16 + 12, true), outQuat);
  return true;
}

/** base64 helpers for storing ghost frames in localStorage (works in browser + node). */
export function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    bin += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  if (typeof btoa === 'function') return btoa(bin);
  return Buffer.from(bin, 'binary').toString('base64');
}

export function base64ToBuffer(b64: string): ArrayBuffer {
  let bin: string;
  if (typeof atob === 'function') bin = atob(b64);
  else bin = Buffer.from(b64, 'base64').toString('binary');
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
}
